import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { repeat } from 'ramda';
import InlineButtonPanel from '@RHEnvironmental/pattern-library/dist/js/components/InlineButtonPanel';
import InputContainer from '@RHEnvironmental/pattern-library/dist/js/components/InputContainer';
import LoadingIndicator from '@RHEnvironmental/pattern-library/dist/js/components/LoadingIndicator';
import Overlay from '@RHEnvironmental/pattern-library/dist/js/components/Overlay';
import SelectBox from '@RHEnvironmental/pattern-library/dist/js/components/SelectBox';
import TextAreaField from '@RHEnvironmental/pattern-library/dist/js/components/TextAreaField';
import Tabs from '@RHEnvironmental/pattern-library/dist/js/components/Tabs';
import Tab from '@RHEnvironmental/pattern-library/dist/js/components/Tab';
import Button from '@RHEnvironmental/pattern-library/dist/js/components/Button';
import AddressInput from '../../modules/AddressInput';
import AddressLookup from '../AddressLookup';
import AlertBox from '../../modules/AlertBox';
import EpcLookup from '../EPCLookup';
import ConfirmModal from '../../modules/ConfirmModal';
import { PROPERTY_TYPE } from '../../../constants/addresses';
import { createUpdateAddress, formRefreshed, hideAddressLookup, refreshFormOptions, reset, showAddressLookup, updateProperty, } from '../../../actions/editPropertyDetails';
import { RADON_LEVELS } from '../../../constants/HHSRS';
import { removeHHSRSScoring } from '../../../actions/inspectionDetail';
const possibleFloors = repeat(undefined, 100);
const EditPropertyDetails = ({ onClose, onSuccess, inspection, HHSRSScoring, }) => {
    const initialForm = {
        initialValues: {
            id: inspection.property.id,
            inspection_id: inspection.id,
            property_type_id: inspection.property.property_type_id,
            property_detachment_id: inspection.property.property_detachment_id,
            property_age_id: inspection.property.property_age_id,
            floors: inspection.property.floors,
            dwellings: inspection.property.dwellings,
            is_hmo: inspection.property.is_hmo,
            description: inspection.property.description,
            radon: inspection.property.radon,
            council_or_rsl_owned: inspection.property.council_or_rsl_owned ?? '',
        },
        initialStatus: {
            errors: {},
        },
    };
    const dispatch = useDispatch();
    const { form, propertyTypes, propertyDetachments, propertyAges, isLoading, isShowingAddressLookup, } = useSelector((state) => state.pages.editPropertyDetails);
    const [showConfirmSave, setShowConfirmSave] = useState({
        visible: false,
        values: null,
        helpers: null,
    });
    const resetShowConfirmSave = () => {
        setShowConfirmSave({
            visible: false,
            values: null,
            helpers: null,
        });
    };
    const checkIfScoringIsAffected = (values, helpers) => {
        let allowSubmit = true;
        if (HHSRSScoring) {
            if (!values.property_type_id || !values.property_age_id) {
                setShowConfirmSave({ visible: true, values, helpers });
                helpers.setSubmitting(false);
                allowSubmit = false;
            }
        }
        if (allowSubmit) {
            dispatch(updateProperty(values, helpers, onSuccess));
        }
    };
    useEffect(() => {
        dispatch(refreshFormOptions);
        dispatch(formRefreshed(initialForm));
        return () => {
            dispatch(reset());
        };
    }, []);
    return (React.createElement(Overlay, { isOpen: true, autoHeight: "true", onClose: () => onClose(), onRequestClose: () => onClose(), title: "Edit property details" }, isLoading ? (React.createElement(LoadingIndicator, { show: true })) : (React.createElement(React.Fragment, null,
        React.createElement(AlertBox, { className: "mb-4", section: "property-details" }),
        React.createElement(Tabs, null,
            React.createElement(Tab, { label: "Details" },
                isShowingAddressLookup ? (React.createElement(AddressLookup, { existingAddress: inspection.property.address, addressableType: PROPERTY_TYPE, addressableId: inspection.property.id, onSave: (address) => dispatch(createUpdateAddress(address)), onCancel: () => dispatch(hideAddressLookup()) })) : (React.createElement(Formik, { onSubmit: (formValues, formHelpers) => checkIfScoringIsAffected(formValues, formHelpers), enableReinitialize: true, initialValues: form.initialValues, initialStatus: form.initialStatus }, ({ values, handleChange, handleSubmit, errors, isSubmitting, setFieldValue, }) => (React.createElement("form", null,
                    React.createElement("input", { type: "hidden", name: "inspection_id", value: inspection.id }),
                    React.createElement(InputContainer, { className: "mb-4" },
                        React.createElement(AddressInput, { address: inspection.property.address, onEdit: () => {
                                dispatch(showAddressLookup());
                            } })),
                    React.createElement(InputContainer, { className: "mb-4" },
                        React.createElement(TextAreaField, { name: "description", id: "description", showLabel: true, placeholder: "Property description...", onChange: handleChange, value: values.description ?? undefined, errors: errors.description }, "Property description")),
                    React.createElement(InputContainer, { className: "mb-4" },
                        React.createElement(SelectBox, { label: "Dwelling type", name: "property_type_id", controlFunc: handleChange, errors: errors.property_type_id },
                            React.createElement("option", { value: "" }, "Not known"),
                            propertyTypes.map((propertyType, index) => (React.createElement("option", { selected: values.property_type_id === propertyType.id, key: index, value: propertyType.id }, propertyType.name))))),
                    React.createElement(InputContainer, { className: "mb-4" },
                        React.createElement(SelectBox, { label: "Is HMO?", name: "is_hmo", controlFunc: (e) => {
                                let value = null;
                                if (e.target.value === 'true')
                                    value = true;
                                if (e.target.value === 'false')
                                    value = false;
                                setFieldValue('is_hmo', value);
                            }, errors: errors.is_hmo },
                            React.createElement("option", { value: "", selected: values.is_hmo === null }, "Not known"),
                            React.createElement("option", { value: "true", selected: values.is_hmo === true }, "Yes"),
                            React.createElement("option", { value: "false", selected: values.is_hmo === false }, "No"))),
                    React.createElement(InputContainer, { className: "mb-4" },
                        React.createElement(SelectBox, { label: "Dwelling age", name: "property_age_id", controlFunc: handleChange, errors: errors.property_age_id },
                            React.createElement("option", { value: "" }, "Not known"),
                            propertyAges.map((propertyAge, index) => (React.createElement("option", { selected: values.property_age_id === propertyAge.id, key: index, value: propertyAge.id }, propertyAge.name))))),
                    React.createElement(InputContainer, { className: "mb-4" },
                        React.createElement(SelectBox, { label: "Property detachment", name: "property_detachment_id", controlFunc: handleChange, errors: errors.property_detachment_id },
                            React.createElement("option", { value: "" }, "Not known"),
                            propertyDetachments.map((propertyDetachment, index) => (React.createElement("option", { selected: values.property_detachment_id ===
                                    propertyDetachment.id, key: index, value: propertyDetachment.id }, propertyDetachment.name))))),
                    React.createElement(InputContainer, { className: "mb-4" },
                        React.createElement(SelectBox, { label: "Floors in building", name: "floors", controlFunc: handleChange, errors: errors.floors },
                            React.createElement("option", { value: "" }, "Not known"),
                            possibleFloors.map((_, i) => (React.createElement("option", { selected: values.floors === i + 1, key: i + 1, value: i + 1 }, i + 1))))),
                    React.createElement(InputContainer, { className: "mb-4" },
                        React.createElement(SelectBox, { label: "Total dwellings in building", name: "dwellings", controlFunc: handleChange, errors: errors.dwellings },
                            React.createElement("option", { value: "" }, "Not known"),
                            possibleFloors.map((_, i) => (React.createElement("option", { selected: values.dwellings === i + 1, key: i + 1, value: i + 1 }, i + 1))))),
                    React.createElement(InputContainer, { className: "mb-4" },
                        React.createElement(SelectBox, { label: "Is the dwelling Council or Registered Social Landlord (RSL) owned?", name: "council_or_rsl_owned", controlFunc: (e) => {
                                let value = null;
                                if (e.target.value === 'true')
                                    value = true;
                                if (e.target.value === 'false')
                                    value = false;
                                setFieldValue('council_or_rsl_owned', value);
                            }, errors: errors.is_hmo },
                            React.createElement("option", { value: "", selected: values.council_or_rsl_owned === null }, "Not known"),
                            React.createElement("option", { value: "true", selected: values.council_or_rsl_owned === true }, "Yes"),
                            React.createElement("option", { value: "false", selected: values.council_or_rsl_owned === false }, "No"))),
                    React.createElement(InputContainer, { className: "mb-4" },
                        React.createElement(SelectBox, { label: "Radon level", name: "radon", controlFunc: handleChange, errors: errors.radon },
                            React.createElement("option", { value: "" }, "Not known"),
                            RADON_LEVELS.map((radonLevel, index) => (React.createElement("option", { selected: values.radon === radonLevel, key: index, value: radonLevel }, radonLevel))))),
                    React.createElement(InlineButtonPanel, null,
                        React.createElement(Button, { className: "button--white", onClick: onClose }, "Cancel"),
                        React.createElement(Button, { onClick: handleSubmit, disabled: isSubmitting }, "Save")))))),
                React.createElement(ConfirmModal, { className: undefined, show: showConfirmSave.visible, title: "Warning", messageBody: "Setting Dwelling type or Dwelling age to Not known will automatically remove any existing HHSRS Scoring. This cannot be undone.", confirmText: "Save anyway", theme: "warning", onCancel: resetShowConfirmSave, onConfirm: () => {
                        if (!showConfirmSave.helpers) {
                            return;
                        }
                        dispatch(updateProperty(showConfirmSave.values, showConfirmSave.helpers, onSuccess));
                        dispatch(removeHHSRSScoring(inspection.id));
                        resetShowConfirmSave();
                    } })),
            React.createElement(Tab, { label: "EPC" },
                React.createElement(EpcLookup, { onCancel: () => {
                        onClose();
                    }, property: inspection.property, onSuccess: onSuccess })))))));
};
export default EditPropertyDetails;
